import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { CATEGORY_LABEL, RAIL_LABEL, PAYOUT_RAILS, BBPS_CATEGORIES } from "@/lib/constants";
import { toast } from "sonner";
import { Package as PackageIcon } from "lucide-react";

export default function Packages() {
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/me/package")
      .then(({ data }) => setPkg(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Failed to load package"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <DashboardLayout>
      <div>
        <span className="overline">Pricing</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
          Your price package
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl">
          Platform fees and partner margin applied to each transaction. Contact your account
          manager to switch packages.
        </p>

        {loading || !pkg ? (
          <div className="mt-8 text-muted-foreground">Loading…</div>
        ) : (
          <>
            <div className="mt-8 surface-card p-6 flex items-center gap-4">
              <div className="h-10 w-10 rounded-md bg-primary text-primary-foreground grid place-items-center">
                <PackageIcon className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="font-display text-xl font-bold">{pkg.name}</div>
                <div className="text-sm text-muted-foreground">{pkg.description || "—"}</div>
              </div>
              {pkg.is_default && <Badge>Default package</Badge>}
            </div>

            <div className="mt-8 grid lg:grid-cols-2 gap-6">
              <FeeTable
                title="BBPS categories"
                rows={BBPS_CATEGORIES.map((c) => ({ key: c.id, label: c.label, fees: (pkg.category_fees || {})[c.id] }))}
              />
              <FeeTable
                title="Payout rails"
                rows={PAYOUT_RAILS.map((r) => ({ key: r.id, label: r.label, fees: (pkg.payout_fees || {})[r.id] }))}
              />
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function FeeTable({ title, rows }) {
  return (
    <div className="surface-card overflow-hidden">
      <div className="p-5 pb-3">
        <span className="overline">{title}</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Item</TableHead>
            <TableHead className="text-right">Platform fee</TableHead>
            <TableHead className="text-right">Partner margin</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ key, label, fees }) => (
            <TableRow key={key}>
              <TableCell>{label}</TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs">
                ₹{fees?.platform_fee_flat ?? 0} + {fees?.platform_fee_percent ?? 0}%
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums text-xs text-success">
                ₹{fees?.partner_margin_flat ?? 0} + {fees?.partner_margin_percent ?? 0}%
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
